import type { CaptureSession, WorkflowType } from '@/shared/capture/capture-session';
import {
  CAPTURE_SESSION_STATUSES,
  createCaptureSession,
  isDeletableLocalSession,
  WORKFLOW_TYPES,
} from '@/shared/capture/capture-session';
import { type FieldDatabase, SYNC_QUEUE_STATUSES } from '@/shared/db/schema';

export class CaptureSessionRepository {
  constructor(private readonly database: FieldDatabase) {}

  async createDraft(input: { id: string; workflowType: WorkflowType; locationId?: number; createdAt: string }) {
    const session = createCaptureSession({
      id: input.id,
      workflowType: input.workflowType,
      locationId: input.locationId,
      sourceTime: input.createdAt,
    });

    await this.save(session);

    return session;
  }

  async save(session: CaptureSession) {
    await this.database.put('capture_sessions', session);
  }

  get(id: string) {
    return this.database.get('capture_sessions', id);
  }

  async deleteLocalDraft(id: string) {
    const session = await this.get(id);

    if (!session) {
      return;
    }

    if (!isDeletableLocalSession(session)) {
      throw new Error(`Capture session ${id} is not a local draft.`);
    }

    if ((await this.database.get('sync_queue', id))?.status === SYNC_QUEUE_STATUSES.syncing) {
      throw new Error(`Capture session ${id} is being synchronized.`);
    }

    const photoBlobIds = new Set(
      session.photos.flatMap(photo =>
        photo.thumbnailBlobId ? [photo.localBlobId, photo.thumbnailBlobId] : [photo.localBlobId],
      ),
    );

    await Promise.all([
      this.database.delete('capture_sessions', id),
      this.database.delete('sync_queue', id),
      ...[...photoBlobIds].map(photoId => this.database.delete('photo_blobs', photoId)),
    ]);
  }

  async update(id: string, updater: (session: CaptureSession) => CaptureSession) {
    const session = await this.get(id);

    if (!session) {
      throw new Error(`Capture session ${id} was not found.`);
    }

    const updated = updater(session);
    await this.save(updated);

    return updated;
  }

  async listDrafts() {
    const sessions = await this.database.getAll('capture_sessions');

    return sessions
      .filter(session => session.status === CAPTURE_SESSION_STATUSES.draft)
      .toSorted(compareUpdatedAtDescending);
  }

  async listQueuedInventorizationSessions(input: { limit?: number } = {}) {
    const sessions = await this.database.getAll('capture_sessions');
    const queued = sessions
      .filter(
        session =>
          session.workflowType === WORKFLOW_TYPES.inventorize && session.status === CAPTURE_SESSION_STATUSES.queued,
      )
      .toSorted(compareSubmittedOrUpdatedAtDescending);

    return typeof input.limit === 'number' ? queued.slice(0, input.limit) : queued;
  }

  async listInventorizationSessions() {
    const sessions = await this.database.getAll('capture_sessions');

    return sessions
      .filter(session => session.workflowType === WORKFLOW_TYPES.inventorize)
      .toSorted(compareSubmittedOrUpdatedAtDescending);
  }
}

function compareUpdatedAtDescending(left: CaptureSession, right: CaptureSession) {
  return right.updatedAt.localeCompare(left.updatedAt);
}

function compareSubmittedOrUpdatedAtDescending(left: CaptureSession, right: CaptureSession) {
  return (right.submittedAt ?? right.updatedAt).localeCompare(left.submittedAt ?? left.updatedAt);
}
